/** Coalesce rapid seeks so scrubbing doesn't thrash the decoder. */
export function createSeekController(
  media: HTMLMediaElement,
  onSettled?: () => void
) {
  let pending: number | null = null;
  let running = false;
  let generation = 0;

  const seekOnce = (time: number) =>
    new Promise<void>((resolve) => {
      const t = Math.max(0, time);
      if (Math.abs(media.currentTime - t) < 0.02 && media.readyState >= 2) {
        resolve();
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        media.removeEventListener('seeked', finish);
        media.removeEventListener('loadeddata', finish);
        resolve();
      };

      media.addEventListener('seeked', finish);
      media.addEventListener('loadeddata', finish);

      try {
        const v = media as HTMLMediaElement & { fastSeek?: (n: number) => void };
        if (typeof v.fastSeek === 'function') {
          v.fastSeek(t);
        } else {
          media.currentTime = t;
        }
      } catch {
        finish();
        return;
      }

      window.setTimeout(finish, 90);
    });

  return {
    request(time: number) {
      pending = time;
      if (running) return;
      running = true;
      const gen = ++generation;
      void (async () => {
        while (pending !== null && gen === generation) {
          const next = pending;
          pending = null;
          await seekOnce(next);
          onSettled?.();
        }
        if (gen === generation) running = false;
      })();
    },
    destroy() {
      generation++;
      pending = null;
      running = false;
    },
  };
}

export type SeekController = ReturnType<typeof createSeekController>;
